import AppBackdrop from "./AppBackdrop"
import React from "react"
import { useSelector } from "react-redux"
import { Route, Redirect, RouteProps, useLocation } from "react-router-dom"
import { getAuthState } from "../users/store/selectors"
import { useAppDispatch } from "../store"
import { FetchUser, AutoRefreshAccessToken } from "../users/store/actions"
import { Alert } from "@material-ui/lab"
import { unwrapResult } from "@reduxjs/toolkit"
import { FetchContextOptions } from "../context-options/store/actions"
import { FetchUserMemberships } from "../memberships/store"
import { FetchMembershipPermissions } from "../permissions/store/actions"
import { FetchUserInvitations } from "../invitations/store/actions"
import { IUserNormalized } from "../users/interfaces/user-normalized"
import { paths } from "../paths"
import { IMembershipResponse } from "../memberships/interfaces/membership-response"
import { useTranslation } from "react-i18next"
import { usePageTitle } from "../hooks/use-page-title"
import { useOptions } from "../options/hooks/use-options"
import { unknownErrorToString } from "../utils/errors"
import { DefaultAuthenticatedRedirect } from "./DefaultAuthenticatedRedirect"

type AuthRequiredRouteProps = RouteProps & {
  component: React.ComponentType<any>
}

type InitialDataLoaderProps = {
  component: React.ComponentType<any>
  user: IUserNormalized
}

type LoadingStatus = "idle" | "loading" | "fulfilled" | "rejected"

const DEFAULT_AUTH_ERROR = "An error occurred while fetching user data"
const NOT_AUTHENTICATED_ERROR = "User is not authenticated"
const AUTH_GATE_ERROR = "An unexpected error occurred at AuthenticatedRoute Component"

const useLanguageSync = (user: IUserNormalized | null) => {
  const { i18n } = useTranslation()
  React.useEffect(() => {
    if (user && user.language !== i18n.language) {
      i18n.changeLanguage(user.language)
    }
  }, [user, i18n])
}

const useEnsureAuthenticatedUser = () => {
  const dispatch = useAppDispatch()
  const { user, isAuthenticated, authError } = useSelector(getAuthState)

  const [status, setStatus] = React.useState<LoadingStatus>("idle")
  const [localError, setLocalError] = React.useState<string | null>(null)
  const hasStartedRef = React.useRef(false)

  React.useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!isAuthenticated) {
        setLocalError(NOT_AUTHENTICATED_ERROR)
        setStatus("rejected")
        return
      }

      if (hasStartedRef.current) return
      hasStartedRef.current = true

      setStatus("loading")

      try {
        await dispatch(AutoRefreshAccessToken()).unwrap()
        await dispatch(FetchUser()).unwrap()

        if (!cancelled) {
          setStatus("fulfilled")
        }
      } catch (err) {
        if (!cancelled) {
          setLocalError(unknownErrorToString(err, DEFAULT_AUTH_ERROR))
          setStatus("rejected")
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [dispatch, isAuthenticated])

  return {
    error: authError ?? localError,
    isLoading: status === "loading",
    isAuthenticated,
    user,
  }
}

const AuthGate = ({ component: Component, ...rest }: AuthRequiredRouteProps) => {
  const { error, isLoading, isAuthenticated, user } = useEnsureAuthenticatedUser()
  useLanguageSync(user) // Set application language according to user's preference
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const mainContextId = searchParams.get("mainContext")
  const hasValidMainContext = typeof mainContextId === "string" && mainContextId.length > 0

  if (!isAuthenticated) {
    return <Redirect to={{ pathname: paths.login(), state: { from: rest.location } }} />
  }

  if (isLoading) {
    return <AppBackdrop timeout={60000} />
  }

  if (error) {
    return <ErrorComponent message={error} />
  }

  // If user tries to access path with mainContext like `/home`, we redirect them to `/home?mainContext=<userId>`
  // so that app can load and render the page, instead of breaking due to missing context.
  if (user && !hasValidMainContext) {
    // eslint-disable-next-line no-console
    console.log(`\nMain context missing at AuthGate\nRedirecting to default context ?mainContext=${user.id}\n\n`)

    return <DefaultAuthenticatedRedirect user={user} />
  }

  if (user && hasValidMainContext) {
    return <InitialDataLoader {...rest} component={Component} user={user} />
  }

  return <ErrorComponent message={AUTH_GATE_ERROR} />
}

const AuthRequiredRoute = ({ component: Component, ...rest }: AuthRequiredRouteProps) => {
  usePageTitle() // Sets page title based on current route
  return <Route {...rest} render={(props) => <AuthGate {...props} component={Component} />} />
}

const InitialDataLoader = ({ component: Component, user, ...rest }: InitialDataLoaderProps) => {
  const dispatch = useAppDispatch()
  const [loaderStatus, setLoaderStatus] = React.useState<LoadingStatus>("loading")
  const isLoading = loaderStatus === "loading"
  const isRejected = loaderStatus === "rejected"
  const { queryMetadata } = useOptions()

  React.useEffect(() => {
    if (user.isActive) {
      loader()
    } else {
      setLoaderStatus("fulfilled")
    }

    async function loader() {
      try {
        const resolvedActions = await Promise.all([
          dispatch(FetchUserMemberships()),
          dispatch(FetchContextOptions()),
          dispatch(FetchUserInvitations()),
        ])
        const unwrappedResults = resolvedActions.map(unwrapResult)
        const userMemberships = unwrappedResults[0] as IMembershipResponse[]
        const userMembershipPermissionPromises = userMemberships.map((membership) => {
          return dispatch(FetchMembershipPermissions(membership.id))
        })
        const dispatchResults = await Promise.all(userMembershipPermissionPromises)
        dispatchResults.forEach((result) => unwrapResult(result))
        setLoaderStatus("fulfilled")
      } catch (error) {
        setLoaderStatus("rejected")
      }
    }
  }, [dispatch, user])

  if (isLoading || queryMetadata.isLoading) return <AppBackdrop />
  if (isRejected) return <ErrorComponent message="An error occurred while loading initial data" />
  return <Component {...rest} />
}

const ErrorComponent = ({ message }: { message: string }) => {
  return (
    <div style={{ maxWidth: 550, padding: 20, margin: "80px auto" }}>
      <Alert severity="error">An error occurred: {message}</Alert>
    </div>
  )
}

export default AuthRequiredRoute
